import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
  CaretLeft, PencilSimple, Ticket, CalendarBlank, Barbell, Warning, Medal, Phone, Envelope, User as UserIcon, Key, LockOpen, Copy, Plus, Trash, Package
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useCardPayment } from '../lib/cardTerminal';
import { getLevelBadgeClass } from '../utils/level';
import AdminRatingHistoryPanel from '../components/AdminRatingHistoryPanel';
import { LevelTestPicker } from '../components/LevelTestPicker';
import { LevelVerifyControl } from '../components/LevelVerifyControl';

const CustomerDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t, language, API, user: currentUser } = useAuth();
  const payByCard = useCardPayment();
  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('bookings');
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwResult, setPwResult] = useState(null); // { password } after save

  // Abos (subscriptions)
  const [userSubs, setUserSubs] = useState([]);
  const [showSellSub, setShowSellSub] = useState(false);
  // Price defaults come from the public website pricing.
  //   Court: 58 CHF off-peak / 68 CHF peak (per hour)
  //   Training: TRAINING_PRICE_MATRIX (per participant, 60 min slot)
  //   Golf Jahres-Abo: 999 solo / 1499 group (1 year unlimited)
  const COURT_PRICE = { off_peak: 58, peak: 68 };
  const TRAINING_MATRIX_60 = {
    1: { off_peak: 120, peak: 140 },
    2: { off_peak: 70,  peak: 80  },
    3: { off_peak: 55,  peak: 70  },
    4: { off_peak: 50,  peak: 60  },
  };
  const computeSubDefaultPrice = (type, isPeak, participants) => {
    const tier = isPeak ? 'peak' : 'off_peak';
    if (type === 'court') return COURT_PRICE[tier];
    if (type === 'golf_yearly_solo') return 999;
    if (type === 'golf_yearly_group') return 1499;
    const row = TRAINING_MATRIX_60[participants] || TRAINING_MATRIX_60[1];
    return row[tier];
  };
  const [subForm, setSubForm] = useState({
    type: 'training',
    paid_lessons: '10',
    bonus_lessons: '1',
    price_per_lesson: '120',
    payment_method: 'cash',
    is_peak: false,
    participants: 1,
  });
  const [subLoading, setSubLoading] = useState(false);
  const [subDetail, setSubDetail] = useState(null);
  const [subDetailLoading, setSubDetailLoading] = useState(false);

  const loadUserSubs = async () => {
    try {
      const { data: s } = await API.get(`/subscriptions?user_id=${userId}`);
      setUserSubs(s);
    } catch (_) { setUserSubs([]); }
  };

  useEffect(() => { if (userId) loadUserSubs(); }, [userId]);

  const handleSellSub = async () => {
    try {
      setSubLoading(true);
      const isYearly = subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group';
      const price = parseFloat(subForm.price_per_lesson) || 0;
      if (subForm.payment_method === 'card') {
        const total = isYearly ? price : (parseInt(subForm.paid_lessons) || 0) * price;
        const term = await payByCard(total, `Abo ${userId}`);
        if (!term.ok) { toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason} — ${L('abo not sold', 'Abo nicht verkauft')}`, { duration: 10000 }); return; }
      }
      if (isYearly) {
        if (price <= 0) {
          toast.error(L('Invalid price', 'Ungültiger Preis'));
          return;
        }
        await API.post('/subscriptions', {
          user_id: userId,
          type: subForm.type,
          total_price_override: price,
          payment_method: subForm.payment_method,
          cash_amount: subForm.payment_method === 'cash' ? price : 0,
          card_amount: subForm.payment_method === 'card' ? price : 0,
        });
      } else {
        const paid = parseInt(subForm.paid_lessons) || 0;
        const bonus = parseInt(subForm.bonus_lessons) || 0;
        if (paid <= 0 || price <= 0) {
          toast.error(L('Invalid quantity or price', 'Ungültige Menge oder Preis'));
          return;
        }
        if (bonus < 0) {
          toast.error(L('Bonus lessons cannot be negative', 'Gratis-Lektionen dürfen nicht negativ sein'));
          return;
        }
        const totalLessons = paid + bonus;
        const totalPrice = paid * price;
        await API.post('/subscriptions', {
          user_id: userId,
          type: subForm.type,
          total_lessons: totalLessons,
          paid_lessons: paid,
          price_per_lesson: price,
          payment_method: subForm.payment_method,
          cash_amount: subForm.payment_method === 'cash' ? totalPrice : 0,
          card_amount: subForm.payment_method === 'card' ? totalPrice : 0,
        });
      }
      toast.success(L('Abo sold and added to cash register!', 'Abo verkauft und in Kasse erfasst!'));
      setShowSellSub(false);
      setSubForm({
        type: 'training',
        paid_lessons: '10',
        bonus_lessons: '1',
        price_per_lesson: '120',
        payment_method: 'cash',
        is_peak: false,
        participants: 1,
      });
      await loadUserSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setSubLoading(false); }
  };

  const handleDeleteSub = async (subId) => {
    if (!window.confirm(L('Delete this subscription? This also removes the related cash register entry.', 'Abo löschen? Der zugehörige Kassen-Eintrag wird ebenfalls entfernt.'))) return;
    try {
      await API.delete(`/subscriptions/${subId}`);
      toast.success(L('Subscription deleted', 'Abo gelöscht'));
      await loadUserSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const openSubDetail = async (subId) => {
    try {
      setSubDetailLoading(true);
      setSubDetail({ id: subId, loading: true });
      const { data: sd } = await API.get(`/subscriptions/${subId}`);
      setSubDetail(sd);
      // Patch the fresh sub back into the card list so the compact Abo
      // card on the profile shows the SAME numbers (balance, status,
      // used_lessons) as the detail dialog without the admin having to
      // refresh the page. Fixes the "card looks stale until I click
      // details" complaint.
      setUserSubs(prev => prev.map(s => s.id === subId ? { ...s, ...sd } : s));
    } catch (e) {
      toast.error(L('Failed to load subscription', 'Abo konnte nicht geladen werden'));
      setSubDetail(null);
    } finally { setSubDetailLoading(false); }
  };

  // Reconcile-from-log: Dry-run preview + apply. Diagnoses "phantom"
  // deductions (log entries pointing at deleted bookings that were never
  // marked refunded) and corrects used_lessons / balance_remaining.
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const runReconcilePreview = async (subId) => {
    try {
      setReconcileLoading(true);
      const { data } = await API.post(`/admin/subscriptions/${subId}/reconcile-from-log?apply=false`);
      setReconcilePreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Preview failed', 'Vorschau fehlgeschlagen'));
    } finally { setReconcileLoading(false); }
  };
  const applyReconcile = async (subId) => {
    try {
      setReconcileLoading(true);
      const { data } = await API.post(`/admin/subscriptions/${subId}/reconcile-from-log?apply=true`);
      toast.success(data.noop
        ? L('Already consistent — nothing changed.', 'Bereits konsistent — nichts geändert.')
        : L('Abo reconciled.', 'Abo abgeglichen.'));
      setReconcilePreview(null);
      // Reload the sub-detail so the UI reflects the new numbers.
      await openSubDetail(subId);
      await loadUserSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Apply failed', 'Anwenden fehlgeschlagen'));
    } finally { setReconcileLoading(false); }
  };

  useEffect(() => { loadData(); }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: d } = await API.get(`/users/${userId}`);
      setData(d);
    } catch (e) {
      toast.error(L('User not found', 'Benutzer nicht gefunden'));
      navigate('/admin/users');
    } finally { setLoading(false); }
  };

  const openEdit = () => {
    const u = data.user;
    setEditForm({
      name: u.name || '', email: u.email || '', phone: u.phone || '',
      role: u.role || 'customer', player_level: String(u.player_level || 3),
      credits_balance: String(u.credits_balance || 0),
    });
    setShowEdit(true);
  };

  const handleSave = async () => {
    try {
      const u = data.user;
      const payload = {};
      if (editForm.name !== u.name) payload.name = editForm.name;
      if (editForm.email !== u.email) payload.email = editForm.email;
      if (editForm.phone !== (u.phone || '')) payload.phone = editForm.phone;
      if (editForm.role !== u.role) payload.role = editForm.role;
      if (parseFloat(editForm.player_level) !== u.player_level) payload.player_level = parseFloat(editForm.player_level);
      if (parseFloat(editForm.credits_balance) !== (u.credits_balance || 0)) payload.credits_balance = parseFloat(editForm.credits_balance);
      if (Object.keys(payload).length === 0) { toast.info(L('No changes', 'Keine Änderungen')); setShowEdit(false); return; }
      await API.put(`/users/${userId}`, payload);
      toast.success(L('Profile updated!', 'Profil aktualisiert!'));
      setShowEdit(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const handleResetPassword = async (useDefault = false) => {
    const pw = useDefault ? null : pwInput.trim();
    if (!useDefault && (!pw || pw.length < 6)) {
      toast.error(L('Password must be at least 6 characters', 'Passwort muss mindestens 6 Zeichen haben'));
      return;
    }
    try {
      setPwLoading(true);
      const body = useDefault ? {} : { new_password: pw };
      const { data: res } = await API.put(`/admin/users/${userId}/reset-password`, body);
      const finalPw = res.default_password || pw;
      setPwResult({ password: finalPw, isDefault: !!res.is_default });
      toast.success(L('Password updated', 'Passwort aktualisiert'));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setPwLoading(false); }
  };

  const closePwDialog = () => {
    setShowPwDialog(false);
    setPwInput('');
    setPwResult(null);
  };

  if (loading) return <div className="p-12 text-center text-slate-400">{L('Loading...', 'Laden...')}</div>;
  if (!data) return null;

  const { user, bookings, vouchers } = data;

  // Sort bookings so the closest date is on top and the furthest away at the
  // bottom (staff request Feb 2026). Upcoming bookings come first ordered
  // by soonness (today → next week → next month), followed by past bookings
  // ordered most-recent first so admins still see recent history right
  // below the upcoming block without scrolling to the ancient stuff.
  const _bookingDateTime = (b) => new Date(`${b.date}T${b.start_time || '00:00'}`);
  const _todayStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
  const _isFuture = (b) => _bookingDateTime(b) >= _todayStart;
  const _byUpcomingThenRecent = (a, b) => {
    const af = _isFuture(a), bf = _isFuture(b);
    if (af !== bf) return af ? -1 : 1;
    const da = _bookingDateTime(a).getTime();
    const dbb = _bookingDateTime(b).getTime();
    return af ? da - dbb : dbb - da;
  };
  const regularBookings = bookings.filter(b => b.booking_type !== 'training').sort(_byUpcomingThenRecent);
  const trainingLessons = bookings.filter(b => b.booking_type === 'training').sort(_byUpcomingThenRecent);
  const activeVouchers = vouchers.filter(v => (v.remaining_balance || 0) > 0);
  const totalSpent = bookings.filter(b => b.status !== 'cancelled').reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-detail">
      {/* Back Button */}
      <button onClick={() => navigate('/admin/users')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium" data-testid="back-to-users">
        <CaretLeft size={16} /> {L('Back to Users', 'Zurück zu Benutzer')}
      </button>

      {/* Profile Header */}
      <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 bg-emerald-50 border-2 border-emerald-200 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-black text-emerald-600">{user.name?.charAt(0)?.toUpperCase()}</span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{user.name}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="flex items-center gap-1.5 text-sm text-slate-500"><Envelope size={14} /> {user.email}</span>
                  {user.phone && <span className="flex items-center gap-1.5 text-sm text-slate-500"><Phone size={14} /> {user.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  onClick={async () => {
                    try {
                      const { data: r } = await API.post(`/admin/users/${userId}/unlock`);
                      toast.success(
                        r.cleared > 0
                          ? L(`Unlocked (cleared ${r.cleared})`, `Entsperrt (${r.cleared} Einträge)`)
                          : L('No lockout was active', 'Keine Sperre aktiv')
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
                    }
                  }}
                  variant="outline"
                  className="border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="unlock-user-btn"
                  title={L('Clear brute-force lockout so the user can log in immediately', 'Sperre nach zu vielen Fehlversuchen aufheben — User kann sofort einloggen')}
                >
                  <LockOpen size={16} className="mr-2" /> {L('Unlock', 'Entsperren')}
                </Button>
                <Button onClick={() => setShowPwDialog(true)} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="reset-password-btn">
                  <Key size={16} className="mr-2" /> {L('Password', 'Passwort')}
                </Button>
                <Button onClick={openEdit} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="edit-profile-btn">
                  <PencilSimple size={16} className="mr-2" /> {L('Edit', 'Bearbeiten')}
                </Button>
                {/* Delete — admin ONLY. Trainers, staff (Empfang) never see this button. */}
                {currentUser?.role === 'admin' && user?.role === 'customer' && (
                  <Button
                    onClick={() => { setDeleteConfirmName(''); setShowDeleteDialog(true); }}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                    data-testid="delete-user-btn"
                    title={L('Permanently delete this profile (admin only)', 'Profil endgültig löschen (nur Admin)')}
                  >
                    <Trash size={16} className="mr-2" /> {L('Delete', 'Löschen')}
                  </Button>
                )}
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="px-3 py-1 text-xs font-bold uppercase rounded-lg bg-slate-100 text-slate-600">{user.role}</span>
              <span className={`level-badge ${getLevelBadgeClass(user.player_level)}`}>
                <Medal size={12} className="inline mr-1" /> {L('Level', 'Level')} {user.player_level}
              </span>
              <span className="px-3 py-1 text-xs font-bold rounded-lg bg-emerald-50 text-emerald-600">
                {L('Credits', 'Guthaben')}: {(user.credits_balance || 0).toFixed(0)} CHF
              </span>
              {user.is_member && (
                <span className="px-3 py-1 text-xs font-bold rounded-lg bg-sky-50 text-sky-700 border border-sky-200" data-testid="member-since-badge">
                  {L('Member', 'Mitglied')}{user.member_since ? ` ${L('since', 'seit')} ${format(new Date(`${user.member_since}T00:00:00`), 'dd.MM.yyyy')}` : ''}
                  {user.member_until ? ` · ${L('until', 'bis')} ${format(new Date(`${user.member_until}T00:00:00`), 'dd.MM.yyyy')}` : ''}
                </span>
              )}
              {!user.is_junior && <LevelTestPicker key={user.id} user={user} API={API} L={L} />}
              <LevelVerifyControl showLevel={false} userId={user.id} info={{ player_level: user.player_level, level_verified: user.level_verified }} API={API} L={L}
                onChanged={(v) => setData(d => ({ ...d, user: { ...d.user, level_verified: v } }))} />
              {user.is_junior && user.junior_membership_type && (() => {
                // Show junior subscription info (yearly / 3-month) with the
                // remaining days so admin sees at a glance who's about to
                // expire.
                const typeLabel = user.junior_membership_type === 'full_year' ? L('Yearly Abo', 'Jahresabo')
                  : user.junior_membership_type === 'three_month' ? L('3-Month Abo', '3-Monats-Abo')
                  : user.junior_membership_type === 'half_year' ? L('Half-Year Abo', 'Halbjahresabo')
                  : user.junior_membership_type;
                const end = user.junior_membership_end ? new Date(user.junior_membership_end) : null;
                const now = new Date();
                let cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                let suffix = '';
                if (end) {
                  const days = Math.floor((end - now) / 86400000);
                  if (days < 0) { cls = 'bg-rose-50 text-rose-700 border-rose-200'; suffix = ` · ${L('expired', 'abgelaufen')}`; }
                  else if (days < 14) { cls = 'bg-amber-50 text-amber-700 border-amber-200'; suffix = ` · ${days} ${L('days left', 'Tage')}`; }
                  else { suffix = ` · ${L('until', 'bis')} ${user.junior_membership_end}`; }
                }
                return (
                  <span
                    className={`px-3 py-1 text-xs font-bold rounded-lg border ${cls}`}
                    data-testid="junior-membership-badge"
                    title={user.junior_membership_start ? `${L('Start', 'Start')}: ${user.junior_membership_start}` : ''}
                  >
                    🎓 {typeLabel}{suffix}
                    {user.junior_training_frequency && (
                      <span className="ml-1 opacity-70">
                        · {user.junior_training_frequency === '1x_week' ? '1×/Wo'
                          : user.junior_training_frequency === '2x_week' ? '2×/Wo'
                          : user.junior_training_frequency === '3x_week' ? '3×/Wo'
                          : user.junior_training_frequency}
                      </span>
                    )}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{bookings.length}</p>
            <p className="text-xs text-slate-500 uppercase font-semibold">{L('Total Bookings', 'Buchungen gesamt')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600">{trainingLessons.length}</p>
            <p className="text-xs text-slate-500 uppercase font-semibold">{L('Lessons', 'Lektionen')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">{userSubs.length}</p>
            <p className="text-xs text-slate-500 uppercase font-semibold">{L('Subscriptions', 'Abos')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{totalSpent.toFixed(0)} CHF</p>
            <p className="text-xs text-slate-500 uppercase font-semibold">{L('Total Spent', 'Gesamtausgaben')}</p>
          </div>
        </div>
      </div>

      {/* Abos (Subscriptions) Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Package size={20} /> {L('Subscriptions (Abos)', 'Abos')} <span className="text-slate-400 font-normal text-base">({userSubs.length})</span>
          </h2>
          <Button
            onClick={() => setShowSellSub(true)}
            size="sm"
            className="bg-purple-500 text-white hover:bg-purple-600 font-semibold"
            data-testid="sell-abo-btn"
          >
            <Plus size={16} className="mr-1" /> {L('Sell Abo', 'Abo verkaufen')}
          </Button>
        </div>
        {userSubs.length === 0 ? (
          <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-6 text-center text-slate-400 italic text-sm">
            {L('No subscriptions yet — click "Sell Abo" to add one', 'Noch keine Abos — Klicke "Abo verkaufen" zum Anlegen')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userSubs.map(s => {
              const isYearly = !!s.is_yearly;
              // CHF-balance model — read the balance fields; fall back to
              // lesson-count math for legacy rows without them.
              const balTotal = Number(s.balance_total || (s.total_lessons || 0) * (s.price_per_lesson || 0));
              const balRemaining = Number(
                s.balance_remaining != null
                  ? s.balance_remaining
                  : Math.max(0, (s.total_lessons || 0) - (s.used_lessons || 0)) * (s.price_per_lesson || 0)
              );
              const pricePer = Number(s.price_per_lesson || 0);
              // How many lessons the customer could still book at the base
              // price — informational, actual bookings can use any price.
              const lessonsLeftApprox = pricePer > 0 ? Math.floor(balRemaining / pricePer) : 0;
              const pct = balTotal > 0 ? ((balTotal - balRemaining) / balTotal) * 100 : 0;
              const exhausted = !isYearly && balRemaining <= 0.01;
              const remainingChf = balRemaining;
              const totalChf = balTotal;

              // Yearly-specific
              const validUntil = s.valid_until ? new Date(s.valid_until) : null;
              const expired = validUntil ? validUntil < new Date() : false;
              const daysLeft = validUntil ? Math.max(0, Math.ceil((validUntil - new Date()) / (1000 * 60 * 60 * 24))) : null;
              const isGolfGroup = s.type === 'golf_yearly_group';

              const typeLabel = isYearly
                ? (isGolfGroup ? (L('Golf Yearly +Guests', 'Golf Jahr · mit Gästen')) : L('Golf Yearly Solo', 'Golf Jahr · Solo'))
                : (s.type === 'training' ? L('Training', 'Training') : 'Court');

              const cardBg = isYearly
                ? (expired ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-amber-50 border-amber-200 hover:bg-amber-100')
                : (exhausted ? 'bg-slate-50 border-slate-200 opacity-70' : s.type === 'training' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100');
              const badgeBg = isYearly
                ? 'bg-amber-100 text-amber-800'
                : (s.type === 'training' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700');
              const barColor = isYearly
                ? (expired ? 'bg-red-400' : 'bg-amber-500')
                : (exhausted ? 'bg-red-400' : s.type === 'training' ? 'bg-blue-500' : 'bg-emerald-500');

              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openSubDetail(s.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openSubDetail(s.id); }}
                  className={`border rounded-2xl shadow-sm p-4 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${cardBg}`}
                  data-testid={`sub-row-${s.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${badgeBg}`}>
                        {typeLabel}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">
                        {isYearly
                          ? (validUntil ? `${L('Valid until', 'Gültig bis')}: ${validUntil.toLocaleDateString('de-CH')}` : '')
                          : (s.type === 'court'
                              ? `${(s.price_per_lesson || 0).toFixed(2)} CHF/${L('court hour', 'Court-Stunde')}`
                              : `${(s.price_per_lesson || 0).toFixed(2)} CHF/${L('lesson', 'Lektion')}`)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isYearly ? (
                        <span className={`text-lg font-extrabold ${expired ? 'text-red-500' : 'text-amber-700'}`}>
                          {s.used_lessons || 0}× <span className="text-xs font-bold text-slate-400">{expired ? L('expired', 'abgelaufen') : L('used', 'genutzt')}</span>
                        </span>
                      ) : (
                        <span className={`text-2xl font-extrabold ${exhausted ? 'text-red-500' : 'text-slate-900'}`}>
                          {remainingChf.toFixed(0)}<span className="text-sm font-bold text-slate-400"> CHF</span>
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteSub(s.id); }}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-md transition-colors"
                        data-testid={`del-sub-${s.id}`}
                        title={L('Delete', 'Löschen')}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                  {!isYearly && (
                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mb-2">
                      <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs">
                    {isYearly ? (
                      <span className="text-slate-600">
                        {expired
                          ? L('Expired', 'Abgelaufen')
                          : `${daysLeft} ${L('days left', 'Tage übrig')}`} · <strong className="text-slate-800">{(s.total_price || 0).toFixed(0)} CHF</strong>
                      </span>
                    ) : (
                      <span className="text-slate-600">
                        {L('Value left', 'Restwert')}: <strong className="text-slate-800">{remainingChf.toFixed(0)} / {totalChf.toFixed(0)} CHF</strong>
                        {pricePer > 0 && (
                          <span className="text-slate-400"> · ≈ {lessonsLeftApprox} {L('lessons', 'Lektionen')}</span>
                        )}
                      </span>
                    )}
                    <span className="text-slate-400">{L('Details', 'Details')} →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Vouchers Section */}
      {vouchers.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Ticket size={20} /> {L('Vouchers', 'Gutscheine')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vouchers.map(v => {
              const remaining = v.remaining_balance ?? v.amount;
              const pct = v.amount > 0 ? (remaining / v.amount) * 100 : 0;
              const isEmpty = remaining <= 0;
              const isLow = pct <= 20 && !isEmpty;
              return (
                <div key={v.id} className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-4" data-testid={`voucher-${v.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-black text-emerald-600 font-mono">{v.code}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${v.voucher_type === 'training' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                      {v.voucher_type || 'credit'}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">{L('Remaining', 'Restbetrag')}</span>
                      <span className={`font-bold ${isEmpty ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-emerald-600'}`}>
                        {remaining.toFixed(0)} / {v.amount.toFixed(0)} CHF
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isEmpty ? 'bg-red-400' : isLow ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  {isEmpty && <p className="text-red-500 text-xs flex items-center gap-1"><Warning size={12} /> {L('Depleted', 'Aufgebraucht')}</p>}
                  {v.description && <p className="text-slate-400 text-xs italic mt-1">"{v.description}"</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs: Bookings / Lessons */}
      <div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit mb-4">
          <button onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'bookings' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-bookings">
            <CalendarBlank size={14} className="inline mr-1.5" /> {L('Court Bookings', 'Platzbuchungen')} ({regularBookings.length})
          </button>
          <button onClick={() => setActiveTab('lessons')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'lessons' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-lessons">
            <Barbell size={14} className="inline mr-1.5" /> {L('Training Lessons', 'Trainingslektionen')} ({trainingLessons.length})
          </button>
          <button onClick={() => setActiveTab('rating')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'rating' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-rating">
            <Medal size={14} className="inline mr-1.5" /> {L('Rating & Level', 'Rating & Level')}
          </button>
        </div>

        {activeTab === 'rating' ? (
          <AdminRatingHistoryPanel userId={userId} />
        ) : (
        <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
          {(activeTab === 'bookings' ? regularBookings : trainingLessons).length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              {activeTab === 'bookings' ? L('No court bookings yet', 'Keine Platzbuchungen') : L('No training lessons yet', 'Keine Trainingslektionen')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Date', 'Datum')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Time', 'Zeit')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Court', 'Platz')}</th>
                    {activeTab === 'lessons' && <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Trainer', 'Trainer')}</th>}
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Price', 'Preis')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Payment', 'Zahlung')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 font-semibold uppercase">{L('Status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTab === 'bookings' ? regularBookings : trainingLessons).map(b => {
                    // Payment-method pill: inspects the richer flags on each
                    // booking so admins can see at a glance HOW the price was
                    // settled (not just "confirmed"). Priority order mirrors
                    // the backend creation flow: abo > voucher > credits >
                    // explicit method > fallback.
                    const paidViaAbo = !!(b.paid_via_subscription_id || (b.paid_via_subscriptions && b.paid_via_subscriptions.length));
                    const paidViaVoucher = !!b.voucher_id;
                    const paidViaCredits = !!b.use_credits;
                    const method = (b.payment_method || '').toLowerCase();
                    let payLabel = '—';
                    let payClass = 'bg-slate-100 text-slate-500';
                    let payTitle = '';
                    if (paidViaAbo) {
                      payLabel = L('Abo', 'Abo');
                      payClass = 'bg-indigo-100 text-indigo-700 border border-indigo-200';
                      const total = b.paid_via_subscriptions && b.paid_via_subscriptions.length
                        ? b.paid_via_subscriptions.reduce((s, x) => s + Number(x.amount_deducted || 0), 0)
                        : Number(b.subscription_deducted || 0);
                      payTitle = total ? `${L('Abo covered', 'Abo deckte')}: ${total.toFixed(2)} CHF` : L('Paid via subscription', 'Bezahlt via Abo');
                    } else if (paidViaVoucher) {
                      payLabel = L('Voucher', 'Gutschein');
                      payClass = 'bg-amber-100 text-amber-700 border border-amber-200';
                      if (b.voucher_deducted) payTitle = `${L('Voucher amount', 'Gutschein-Betrag')}: ${Number(b.voucher_deducted).toFixed(2)} CHF`;
                    } else if (paidViaCredits) {
                      payLabel = L('Credits', 'Guthaben');
                      payClass = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
                    } else if (method === 'cash') {
                      payLabel = L('Cash', 'Bar');
                      payClass = 'bg-slate-100 text-slate-700 border border-slate-200';
                    } else if (method === 'card') {
                      payLabel = L('Card', 'Karte');
                      payClass = 'bg-blue-100 text-blue-700 border border-blue-200';
                    } else if (method === 'online' || method === 'datatrans') {
                      payLabel = L('Online', 'Online');
                      payClass = 'bg-blue-100 text-blue-700 border border-blue-200';
                    } else if (b.payment_status === 'pending') {
                      payLabel = L('Pending', 'Offen');
                      payClass = 'bg-orange-100 text-orange-700 border border-orange-200';
                    } else if (method) {
                      payLabel = method;
                      payClass = 'bg-slate-100 text-slate-500';
                    }
                    return (
                    <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                        {(() => { try { return format(new Date(b.date), 'dd.MM.yyyy'); } catch { return b.date; } })()}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">{b.start_time} - {b.end_time}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{b.court_name}</td>
                      {activeTab === 'lessons' && <td className="py-3 px-4 text-sm text-emerald-700 font-medium">{b.trainer_name || '-'}</td>}
                      <td className="py-3 px-4 text-sm font-bold text-emerald-600">{(b.amount || 0).toFixed(0)} CHF</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide rounded-md ${payClass}`}
                          title={payTitle}
                          data-testid={`booking-payment-${b.id}`}
                        >
                          {payLabel}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          b.status === 'confirmed' ? 'bg-emerald-50 text-emerald-600'
                          : b.status === 'cancelled' ? 'bg-red-50 text-red-500'
                          : 'bg-slate-100 text-slate-500'
                        }`}>{b.status}</span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-profile-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Edit Profile', 'Profil bearbeiten')}</DialogTitle>
            <DialogDescription className="text-slate-500">{user.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="label-text">{t('name')} *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="edit-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{t('email')} *</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="edit-email" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Phone', 'Telefon')}</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="bg-slate-50 border-slate-200" placeholder="+41 79 123 4567" data-testid="edit-phone" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Role', 'Rolle')}</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="trainer">Trainer</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{t('level')} (1.0 = Pro, 7.9 = {L('Beginner', 'Anfänger')})</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1.0"
                  max="7.9"
                  value={editForm.player_level}
                  onChange={(e) => setEditForm({ ...editForm, player_level: e.target.value })}
                  className="bg-slate-50 border-slate-200"
                  data-testid="edit-level"
                  placeholder={L('e.g. 4.2', 'z.B. 4.2')}
                />
                <p className="text-[11px] text-slate-500">
                  {L('Any value between 1.0 and 7.9 (0.1 steps).', 'Beliebiger Wert zwischen 1.0 und 7.9 (0.1er-Schritte).')}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="label-text">{t('credits')} (CHF)</Label>
                <Input type="number" value={editForm.credits_balance} onChange={(e) => setEditForm({...editForm, credits_balance: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="edit-credits" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEdit(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
              <Button onClick={handleSave} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-profile-btn">
                {L('Save Changes', 'Änderungen speichern')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Profile Dialog — admin only, requires typing the user's name to confirm */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!open) setShowDeleteDialog(false); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="delete-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600">
              {L('Delete profile — cannot be undone', 'Profil löschen — nicht rückgängig zu machen')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {user?.name} · {user?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-700 leading-relaxed">
              {L(
                'All personal data (name, email, phone, address, level, history, notes) is erased permanently. Bookings and cash entries stay anonymised ("Deleted customer") for accounting. Not possible while credit, an active Abo, a voucher balance or an open tab exists.',
                'Alle persönlichen Daten (Name, E-Mail, Telefon, Adresse, Level, Verlauf, Notizen) werden endgültig entfernt. Buchungen und Kasseneinträge bleiben anonym („Gelöschter Kunde“) für die Buchhaltung. Nicht möglich, solange Guthaben, ein aktives Abo, ein Gutschein-Restbetrag oder ein offenes Konto besteht.'
              )}
            </p>
            <div>
              <Label className="label-text text-slate-700">
                {L('Type the name to confirm', 'Zur Bestätigung den Namen tippen')}: <span className="font-bold text-slate-900">{user?.name}</span>
              </Label>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={user?.name}
                className="bg-slate-50 border-slate-200 mt-1"
                autoFocus
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleteLoading}
                className="flex-1 border-slate-200 text-slate-600"
                data-testid="delete-cancel-btn"
              >
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={async () => {
                  if (deleteConfirmName.trim() !== (user?.name || '').trim()) {
                    toast.error(L('Names do not match', 'Namen stimmen nicht überein'));
                    return;
                  }
                  if (!window.confirm(L(
                    `Are you sure? ${user?.name} will be deleted PERMANENTLY and cannot be restored.`,
                    `Bist du sicher? ${user?.name} wird ENDGÜLTIG gelöscht und kann nicht wiederhergestellt werden.`
                  ))) return;
                  setDeleteLoading(true);
                  try {
                    await API.delete(`/admin/users/${userId}`);
                    toast.success(L('Customer permanently deleted', 'Kunde endgültig gelöscht'));
                    navigate('/admin/users');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'), { duration: 10000 });
                    setDeleteLoading(false);
                  }
                }}
                disabled={deleteLoading || deleteConfirmName.trim() !== (user?.name || '').trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="delete-confirm-btn"
              >
                {deleteLoading ? '...' : L('Delete forever', 'Endgültig löschen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPwDialog} onOpenChange={(open) => { if (!open) closePwDialog(); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Change Password', 'Passwort ändern')}</DialogTitle>
            <DialogDescription className="text-slate-500">{user.name} · {user.email}</DialogDescription>
          </DialogHeader>

          {!pwResult ? (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="label-text">{L('New Password', 'Neues Passwort')}</Label>
                <Input
                  type="text"
                  value={pwInput}
                  onChange={(e) => setPwInput(e.target.value)}
                  placeholder={L('Min. 6 characters', 'Min. 6 Zeichen')}
                  className="bg-slate-50 border-slate-200 mt-1"
                  autoFocus
                  data-testid="new-password-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {L('Or generate a one-time password', 'Oder Einmal-Passwort erzeugen')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => handleResetPassword(false)}
                  disabled={pwLoading || pwInput.length < 6}
                  className="w-full bg-emerald-500 text-white hover:bg-emerald-600 font-bold"
                  data-testid="set-password-btn"
                >
                  {pwLoading ? '...' : L('Set Password', 'Passwort setzen')}
                </Button>
                <Button
                  onClick={() => handleResetPassword(true)}
                  disabled={pwLoading}
                  variant="outline"
                  className="w-full border-slate-200 text-slate-600"
                  data-testid="reset-default-btn"
                >
                  {L('Reset to default', 'Auf Standard zurücksetzen')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-2" data-testid="password-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-700">
                  {pwResult.isDefault
                    ? L('One-time password created — client must change it at next login', 'Einmal-Passwort erstellt – Kunde muss es beim nächsten Login ändern')
                    : L('Password updated successfully', 'Passwort erfolgreich aktualisiert')}
                </p>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <code className="flex-1 text-slate-900 font-mono font-bold select-all">{pwResult.password}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(pwResult.password);
                      toast.success(L('Copied!', 'Kopiert!'));
                    }}
                    className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                    title={L('Copy', 'Kopieren')}
                    data-testid="copy-password-btn"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  {L('Share this password with the user securely. They can change it themselves after logging in.',
                     'Teile dieses Passwort sicher mit dem Benutzer. Er kann es nach dem Login selbst ändern.')}
                </p>
              </div>
              <Button onClick={closePwDialog} className="w-full bg-slate-800 text-white hover:bg-slate-900 font-bold" data-testid="pw-close-btn">
                {L('Done', 'Fertig')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Sell Subscription Modal */}
      <Dialog open={showSellSub} onOpenChange={setShowSellSub}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="sell-sub-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Package size={22} /> {L('Sell Subscription', 'Abo verkaufen')}
            </DialogTitle>
            <DialogDescription>{user.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="label-text">{L('Type', 'Typ')}</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setSubForm({
                    ...subForm,
                    type: 'training',
                    price_per_lesson: String(computeSubDefaultPrice('training', subForm.is_peak, subForm.participants)),
                  })}
                  className={`p-3 border rounded-xl text-sm font-semibold transition-all ${subForm.type === 'training' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-type-training"
                >
                  {L('Training', 'Training')}
                </button>
                <button
                  type="button"
                  onClick={() => setSubForm({
                    ...subForm,
                    type: 'court',
                    price_per_lesson: String(computeSubDefaultPrice('court', subForm.is_peak, subForm.participants)),
                  })}
                  className={`p-3 border rounded-xl text-sm font-semibold transition-all ${subForm.type === 'court' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-type-court"
                >
                  Court
                </button>
                <button
                  type="button"
                  onClick={() => setSubForm({
                    ...subForm,
                    type: 'golf_yearly_solo',
                    price_per_lesson: '999',
                  })}
                  className={`p-3 border rounded-xl text-sm font-semibold transition-all ${(subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group') ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-type-golf-yearly"
                >
                  {L('Golf Yearly', 'Golf Jahr')}
                </button>
              </div>
            </div>

            {(subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group') ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSubForm({ ...subForm, type: 'golf_yearly_solo', price_per_lesson: '999' })}
                    className={`p-3 border rounded-xl text-sm font-bold transition-all ${subForm.type === 'golf_yearly_solo' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid="sub-golf-solo"
                  >
                    {L('Solo · 999 CHF', 'Solo · 999 CHF')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSubForm({ ...subForm, type: 'golf_yearly_group', price_per_lesson: '1499' })}
                    className={`p-3 border rounded-xl text-sm font-bold transition-all ${subForm.type === 'golf_yearly_group' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    data-testid="sub-golf-group"
                  >
                    {L('+ Guests · 1499 CHF', 'mit Gästen · 1499 CHF')}
                  </button>
                </div>
                <div className="space-y-2">
                  <Label className="label-text">{L('Total price (editable)', 'Gesamtpreis (editierbar)')} *</Label>
                  <Input
                    type="number" step="1" min="0" value={subForm.price_per_lesson}
                    onChange={(e) => setSubForm({ ...subForm, price_per_lesson: e.target.value })}
                    className="bg-slate-50 border-slate-200" data-testid="sub-golf-price"
                  />
                  <p className="text-[11px] text-slate-500">
                    {L('1 year unlimited access to Golf simulators from today', 'Ein Jahr unlimitierter Zugang zu Golf-Simulatoren ab heute')}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800 font-semibold">{L('Customer pays', 'Kunde bezahlt')}</span>
                    <span className="text-2xl font-extrabold text-amber-700" data-testid="sub-total-price">
                      {(parseFloat(subForm.price_per_lesson) || 0).toFixed(0)} CHF
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-amber-700">
                    <span>{L('Valid until', 'Gültig bis')}</span>
                    <span className="font-mono">
                      {new Date(Date.now() + 365 * 24 * 3600 * 1000).toLocaleDateString('de-CH')}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
            {/* Tariff tier + (Training only) participants */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-text text-xs">{L('Tariff', 'Tarif')}</Label>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { key: false, label: L('Off-peak', 'Off-peak') },
                    { key: true,  label: L('Peak', 'Peak') },
                  ].map(o => (
                    <button
                      key={String(o.key)}
                      type="button"
                      onClick={() => setSubForm({
                        ...subForm,
                        is_peak: o.key,
                        price_per_lesson: String(computeSubDefaultPrice(subForm.type, o.key, subForm.participants)),
                      })}
                      className={`px-2 py-1.5 rounded-md text-xs font-bold border ${subForm.is_peak === o.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600'}`}
                      data-testid={`sub-tier-${o.key ? 'peak' : 'offpeak'}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              {subForm.type === 'training' && (
                <div className="space-y-2">
                  <Label className="label-text text-xs">{L('Players', 'Teilnehmer')}</Label>
                  <div className="grid grid-cols-4 gap-1">
                    {[1, 2, 3, 4].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setSubForm({
                          ...subForm,
                          participants: n,
                          price_per_lesson: String(computeSubDefaultPrice(subForm.type, subForm.is_peak, n)),
                        })}
                        className={`px-2 py-1.5 rounded-md text-xs font-bold border ${subForm.participants === n ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-slate-200 text-slate-600'}`}
                        data-testid={`sub-players-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-text">
                  {subForm.type === 'court'
                    ? L('Paid Slots', 'Bezahlte Std.')
                    : L('Paid Lessons', 'Bezahlte Lektionen')} *
                </Label>
                <Input
                  type="number" min="1" value={subForm.paid_lessons}
                  onChange={(e) => setSubForm({ ...subForm, paid_lessons: e.target.value })}
                  className="bg-slate-50 border-slate-200" data-testid="sub-paid-lessons"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-text text-emerald-700">{L('Bonus (free)', 'Gratis-Lektionen')}</Label>
                <Input
                  type="number" min="0" value={subForm.bonus_lessons}
                  onChange={(e) => setSubForm({ ...subForm, bonus_lessons: e.target.value })}
                  className="bg-emerald-50 border-emerald-200" data-testid="sub-bonus-lessons"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-text">
                {subForm.type === 'court'
                  ? L('Price/Slot (CHF)', 'Preis/Std. (CHF)')
                  : L('Price/Lesson (CHF)', 'Preis/Lektion (CHF)')} *
              </Label>
              <Input
                type="number" step="5" min="0" value={subForm.price_per_lesson}
                onChange={(e) => setSubForm({ ...subForm, price_per_lesson: e.target.value })}
                className="bg-slate-50 border-slate-200" data-testid="sub-price"
              />
            </div>
            {(() => {
              const paid = parseInt(subForm.paid_lessons) || 0;
              const bonus = parseInt(subForm.bonus_lessons) || 0;
              const price = parseFloat(subForm.price_per_lesson) || 0;
              const total = paid + bonus;
              const cost = paid * price;
              const effectivePrice = total > 0 ? cost / total : 0;
              const isCourt = subForm.type === 'court';
              return (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-purple-700 font-semibold">
                      {isCourt ? L('Total Slots', 'Stunden total') : L('Total Lessons', 'Lektionen total')}
                    </span>
                    <span className="text-lg font-bold text-purple-900">
                      {paid}{bonus > 0 && <span className="text-emerald-600"> + {bonus}</span>} = <strong>{total}</strong>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-purple-700 font-semibold">{L('Customer pays', 'Kunde bezahlt')}</span>
                    <span className="text-2xl font-extrabold text-purple-700" data-testid="sub-total-price">
                      {cost.toFixed(0)} CHF
                    </span>
                  </div>
                  {bonus > 0 && total > 0 && (
                    <p className="text-xs text-emerald-700 pt-1 border-t border-purple-200">
                      ≈ {effectivePrice.toFixed(2)} CHF {L('per lesson effective', 'pro Lektion effektiv')} ({L('saves', 'Ersparnis')} {(bonus * price).toFixed(0)} CHF)
                    </p>
                  )}
                </div>
              );
            })()}
              </>
            )}
            <div className="space-y-2">
              <Label className="label-text">{L('Payment', 'Zahlung')}</Label>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => setSubForm({ ...subForm, payment_method: 'cash' })}
                  className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${subForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-pay-cash">
                  {L('Cash', 'Bar')}
                </button>
                <button type="button" onClick={() => setSubForm({ ...subForm, payment_method: 'card' })}
                  className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${subForm.payment_method === 'card' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-pay-card">
                  {L('Card', 'Karte')}
                </button>
                <button type="button" onClick={() => setSubForm({ ...subForm, payment_method: 'invoice' })}
                  className={`p-2.5 border rounded-xl text-sm font-semibold transition-all ${subForm.payment_method === 'invoice' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="sub-pay-invoice">
                  {L('Invoice', 'Rechnung')}
                </button>
              </div>
              <p className="text-xs text-slate-500 pt-1">
                {subForm.payment_method === 'invoice'
                  ? L('Marked as INVOICE in the cash register — no cash/card counted today. Book the payment separately when the invoice is settled.', 'Wird als RECHNUNG in der Kasse markiert — heute wird kein Bar/Karten-Betrag gezählt. Zahlung separat verbuchen wenn die Rechnung beglichen wird.')
                  : L('The amount is registered as PAID in the cash register and appears in today\'s daily report.', 'Der Betrag wird sofort als BEZAHLT in der Kasse erfasst und erscheint in der heutigen Tagesabrechnung.')}
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowSellSub(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
              <Button onClick={handleSellSub} disabled={subLoading} className="flex-1 bg-purple-500 text-white hover:bg-purple-600 font-bold" data-testid="sell-sub-confirm">
                {subLoading ? '...' : L('Sell Abo', 'Abo verkaufen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Detail Modal */}
      <Dialog open={!!subDetail} onOpenChange={(o) => { if (!o) setSubDetail(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="sub-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {subDetail?.type === 'training' ? (
                <span className="inline-block px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-xs font-bold">TRAINING</span>
              ) : subDetail?.type === 'court' ? (
                <span className="inline-block px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold">COURT</span>
              ) : null}
              {L('Subscription details', 'Abo-Details')}
            </DialogTitle>
            <DialogDescription>{subDetail?.user_name}</DialogDescription>
          </DialogHeader>
          {subDetailLoading || subDetail?.loading ? (
            <div className="py-8 text-center text-slate-400 text-sm">{L('Loading…', 'Lädt…')}</div>
          ) : subDetail ? (
            <div className="space-y-4 mt-2">
              {(() => {
                const total = subDetail.total_lessons || 0;
                const used = subDetail.used_lessons || 0;
                const remaining = Math.max(0, total - used);
                const pct = total ? (used / total) * 100 : 0;
                const exhausted = remaining <= 0;
                const ppl = subDetail.price_per_lesson || 0;
                return (
                  <div className={`rounded-xl p-4 ${exhausted ? 'bg-slate-50 border border-slate-200' : subDetail.type === 'training' ? 'bg-blue-50 border border-blue-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <div className="flex items-end justify-between mb-2">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{L('Remaining', 'Verbleibend')}</p>
                        <p className={`text-3xl font-extrabold ${exhausted ? 'text-red-500' : 'text-slate-900'}`}>{remaining}<span className="text-lg font-semibold text-slate-500"> / {total}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{L('Value left', 'Restwert')}</p>
                        <p className="text-2xl font-extrabold text-slate-800">{Number(subDetail.balance_remaining != null ? subDetail.balance_remaining : remaining * ppl).toFixed(0)}<span className="text-sm font-semibold text-slate-500"> CHF</span></p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full ${exhausted ? 'bg-red-400' : subDetail.type === 'training' ? 'bg-blue-500' : 'bg-emerald-500'} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })()}
              <div className="rounded-xl border border-slate-200 p-4 space-y-1.5 text-sm">
                <h4 className="font-bold text-slate-800 mb-2 uppercase text-xs tracking-wide">{L('Purchase', 'Kauf')}</h4>
                <div className="flex justify-between"><span className="text-slate-500">{L('Purchased at', 'Gekauft am')}</span><span className="font-semibold">{subDetail.purchased_at ? new Date(subDetail.purchased_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-CH', { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Sold by', 'Verkauft von')}</span><span className="font-semibold">{subDetail.purchased_by_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Total price', 'Gesamtpreis')}</span><span className="font-semibold">{(subDetail.total_price || 0).toFixed(2)} CHF</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    {subDetail.type === 'court'
                      ? L('Price per court hour', 'Preis pro Court-Stunde')
                      : L('Price per lesson', 'Preis pro Lektion')}
                  </span>
                  <span className="font-semibold">{(subDetail.price_per_lesson || 0).toFixed(2)} CHF</span>
                </div>
                {subDetail.sale_payment_method && (
                  <div className="flex justify-between"><span className="text-slate-500">{L('Payment', 'Zahlung')}</span><span className="font-semibold uppercase">{subDetail.sale_payment_method === 'cash' ? L('Cash', 'Bar') : subDetail.sale_payment_method === 'card' ? L('Card', 'Karte') : subDetail.sale_payment_method === 'invoice' ? L('Invoice', 'Rechnung') : subDetail.sale_payment_method}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">{L('Status', 'Status')}</span>
                  {(() => {
                    // Defensive: don't trust a stale `status` string if the
                    // numbers say the abo clearly still has runway.
                    // Mirrors the backend reconcile rule — balance must be
                    // > 0 AND used < total for the abo to be truly active.
                    const totalLessons = Number(subDetail.total_lessons || 0);
                    const usedLessons = Number(subDetail.used_lessons || 0);
                    const remainingBal = Number(
                      subDetail.balance_remaining != null
                        ? subDetail.balance_remaining
                        : Math.max(0, totalLessons - usedLessons) * Number(subDetail.price_per_lesson || 0)
                    );
                    const trulyExhausted = remainingBal <= 0.01 || (totalLessons > 0 && usedLessons >= totalLessons);
                    return (
                      <span className={`font-semibold ${trulyExhausted ? 'text-red-500' : 'text-emerald-600'}`}>
                        {trulyExhausted ? L('Exhausted', 'Aufgebraucht') : L('Active', 'Aktiv')}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <h4 className="font-bold text-slate-800 mb-3 uppercase text-xs tracking-wide">
                  {L('Usage history', 'Nutzungsverlauf')} <span className="text-slate-400 font-normal">({(subDetail.usage_log || []).length})</span>
                </h4>
                {(subDetail.usage_log || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">{L('No usage yet', 'Noch keine Nutzung')}</p>
                ) : (
                  <div className="space-y-2">
                    {subDetail.usage_log.map((u, idx) => (
                      <div key={idx} className={`flex items-start justify-between gap-3 p-2 rounded-lg ${u.refunded ? 'bg-slate-50 opacity-70' : 'bg-slate-50/50 hover:bg-slate-100'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">
                            #{idx + 1} · {u.booking_date ? (() => {
                              const d = new Date(u.booking_date + 'T00:00:00');
                              return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH', { weekday: 'short', day: '2-digit', month: 'short' });
                            })() : L('Deleted booking', 'Gelöschte Buchung')}
                            {u.booking_start_time && <span className="ml-1.5 text-slate-500 font-normal">@ {u.booking_start_time}</span>}
                            {u.refunded && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">REFUND</span>}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {u.booking_court_name && <span>{u.booking_court_name}</span>}
                            {u.booking_trainer_name && <span> · {L('Trainer', 'Trainer')}: {u.booking_trainer_name}</span>}
                            {u.booking_deleted && <span className="italic">{L('Booking was deleted', 'Buchung wurde gelöscht')}</span>}
                          </p>
                        </div>
                        <div className="text-right text-[10px] text-slate-400 font-mono whitespace-nowrap">
                          {u.used_at ? new Date(u.used_at).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
                          <br />
                          <span>{L('by', 'von')} {u.used_by_name || '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => runReconcilePreview(subDetail.id)}
                  disabled={reconcileLoading || !subDetail.id}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="reconcile-abo-btn"
                >
                  {reconcileLoading
                    ? L('Analyzing…', 'Analysiere…')
                    : L('Reconcile from log', 'Aus Logbuch neu berechnen')}
                </Button>
                <Button variant="outline" onClick={() => setSubDetail(null)} className="border-slate-200">{L('Close', 'Schliessen')}</Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Reconcile-from-log preview modal. Shows dry-run diff + phantom
          entries. User explicitly confirms before anything is persisted. */}
      <Dialog open={!!reconcilePreview} onOpenChange={(o) => { if (!o) setReconcilePreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reconcile-preview-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">{L('Reconcile from usage log', 'Abgleich aus Nutzungs-Log')}</DialogTitle>
            <DialogDescription>
              {L(
                'Recomputes used lessons and balance based on log entries that still have an existing booking. Phantom entries (log pointing at deleted bookings never flagged refunded) are excluded.',
                'Berechnet genutzte Lektionen und Restwert anhand der Log-Einträge, deren Buchung noch existiert. Phantom-Einträge (Log zeigt auf gelöschte Buchung, aber nie als refundiert markiert) werden ausgeschlossen.',
              )}
            </DialogDescription>
          </DialogHeader>
          {reconcilePreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">{L('Current', 'Aktuell')}</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">{L('Used', 'Genutzt')}</span><span className="font-semibold" data-testid="reconcile-cur-used">{reconcilePreview.current.used_lessons}/{reconcilePreview.total_lessons}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{L('Balance', 'Restwert')}</span><span className="font-semibold" data-testid="reconcile-cur-bal">{(reconcilePreview.current.balance_remaining || 0).toFixed(2)} CHF</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-semibold capitalize">{reconcilePreview.current.status}</span></div>
                  </div>
                </div>
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2">{L('Proposed', 'Vorschlag')}</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-600">{L('Used', 'Genutzt')}</span><span className="font-bold text-emerald-800" data-testid="reconcile-new-used">{reconcilePreview.proposed.used_lessons}/{reconcilePreview.total_lessons}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{L('Balance', 'Restwert')}</span><span className="font-bold text-emerald-800" data-testid="reconcile-new-bal">{(reconcilePreview.proposed.balance_remaining || 0).toFixed(2)} CHF</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Status</span><span className="font-bold text-emerald-800 capitalize">{reconcilePreview.proposed.status}</span></div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-3 text-sm">
                <p className="font-bold text-slate-700 mb-2 text-xs uppercase tracking-wide">{L('Log breakdown', 'Logbuch-Aufschlüsselung')}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 py-2">
                    <p className="text-[10px] uppercase font-bold text-emerald-700">{L('Truly used', 'Echt genutzt')}</p>
                    <p className="text-xl font-extrabold text-emerald-800" data-testid="reconcile-count-used">{reconcilePreview.log_counts.truly_used}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                    <p className="text-[10px] uppercase font-bold text-slate-600">{L('Refunded', 'Refundiert')}</p>
                    <p className="text-xl font-extrabold text-slate-700" data-testid="reconcile-count-refunded">{reconcilePreview.log_counts.already_refunded}</p>
                  </div>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 py-2">
                    <p className="text-[10px] uppercase font-bold text-rose-700">{L('Phantom', 'Phantom')}</p>
                    <p className="text-xl font-extrabold text-rose-700" data-testid="reconcile-count-phantom">{reconcilePreview.log_counts.phantom}</p>
                  </div>
                </div>
              </div>

              {reconcilePreview.phantom && reconcilePreview.phantom.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-rose-700 mb-2">
                    {L('Phantom entries to be flagged refunded', 'Phantom-Einträge werden als refundiert markiert')} ({reconcilePreview.phantom.length})
                  </p>
                  <ul className="space-y-1 max-h-48 overflow-y-auto text-[12px]">
                    {reconcilePreview.phantom.map((p) => (
                      <li key={p.idx} className="flex items-center justify-between bg-white rounded-md px-2 py-1 border border-rose-100">
                        <span className="text-slate-600">
                          #{p.idx + 1} · {p.used_at ? new Date(p.used_at).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                          <span className="text-slate-400 ml-1.5">
                            {p.booking_id ? L('booking deleted', 'Buchung gelöscht') : L('no booking id', 'keine Buchungs-ID')}
                          </span>
                        </span>
                        <span className="font-mono text-rose-700 font-bold">−{(p.amount_deducted || 0).toFixed(2)} CHF</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 gap-2">
                <Button variant="outline" onClick={() => setReconcilePreview(null)} className="border-slate-200" data-testid="reconcile-cancel">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={() => applyReconcile(reconcilePreview.subscription_id)}
                  disabled={reconcileLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid="reconcile-apply"
                >
                  {reconcileLoading
                    ? L('Applying…', 'Übernehme…')
                    : L('Apply changes', 'Änderungen übernehmen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetail;
